
import { defineComponent, ref, getCurrentInstance, SetupContext, computed, PropType } from 'vue';
import Toast from 'vant/lib/toast';
import Cookies from 'js-cookie';
// eslint-disable-next-line import/no-extraneous-dependencies
import debounce from 'lodash/debounce';
import CommentList from '../comment/index.vue';
import { formateNum, copyToClipboard, replaceRoutePhotoId } from '@/common/utils';
import { Feed, FeedLiked, UserComment, FeedApiService, AuthorFollowing } from '@/api/feed';
import { sendLog } from '@/common/radar';
import { getLlsidFromExptag } from '@/common/common-params';
import { VideoRef } from '../../type';
import { FeedPageEnum } from '@/store/modules/feed';
import { UserInfoByLogin } from '@/api/user';
import { ActionBarAppSetup } from './scripts/app';
import { ActionBarSeoSetup } from './scripts/seo';
import { ActionType, ActionLogNameMap, ActionItem, IconMap } from './scripts/common';
import { getTemplateConfig } from './template.config';

const ActionBar = defineComponent({
    components: {
        CommentList,
    },

    props: {
        photoId: {
            type: String,
            default: '',
        },
        videoRef: {
            type: Object as PropType<VideoRef> | undefined,
            default: undefined,
        },
        currentIndex: {
            type: Number,
            default: 0,
        },
        feedPage: {
            type: String as PropType<FeedPageEnum> | undefined,
            default: FeedPageEnum.PwaShareNew,
        },
    },

    setup(props, ctx: SetupContext<Array<'onChangeLoginPopupVisible'>>) {
        const { proxy } = getCurrentInstance() || {};
        const setupConfig = proxy?.$isAppEnvironment ? ActionBarAppSetup(props, ctx) : ActionBarSeoSetup(props, ctx);

        const FeedApi = new FeedApiService();
        const commentListVisible = ref(false);
        const commentList = ref<Array<UserComment>>([]);
        const isFollowingDuration = ref(false);
        const query = (proxy?.$route?.query || {}) as { from_page?: string };
        const isFromProfile = query?.from_page === 'profile';

        /**
         * @zh 通知弹窗实验
         * @zh 实验链接 https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/10625/group?docType=12
         */
        const isABTestExpGroup = computed(() => {
            if (typeof window?.Notification?.permission === 'undefined' || window?.Notification?.permission === 'granted') {
                return false;
            }
            return proxy?.$store?.state?.abTestModule?.abTestParams?.notification_check === 'true' && !proxy?.$isAppEnvironment;
        });

        const showNotificationPopup = () => {
            if (!isABTestExpGroup.value) {
                return;
            }
            proxy?.$store?.commit?.('setShowNotificationPopup', true);
        };

        const feedList = computed<Feed[]>(() => {
            const { feedListOrigin } = proxy?.$store?.state?.feedMoudle || {};
            return feedListOrigin?.[props?.feedPage] || [];
        });

        const getFeedByPhotoId = (photoId: string) => {
            const feedItem = feedList.value.find(item => item.photo_id_str && item.photo_id_str === photoId);

            return feedItem;
        };

        const feed = computed(() => {
            return getFeedByPhotoId(props.photoId);
        });

        const userInfoByLogin = computed<UserInfoByLogin>(() => {
            const userInfoByLogin = (proxy?.$store?.state?.userModule?.userInfo || {}) as UserInfoByLogin;
            return userInfoByLogin;
        });

        /**
         * @zh 埋点链接：https://data-track-sgp.corp.kuaishou.com/?#/group/list?appName=TRINITY&model.currentPage=1&model.pageSize=10&groupId=8616&model.pageType=0&model.bizName=GENERAL&tagModel.currentPage=1&tagModel.pageSize=10&viewMode=SIMPLE
         */
        const sendActionLog = (type: ActionType) => {
            const logActionName = ActionLogNameMap[type];
            if (!logActionName) {
                return;
            }

            const { user, userId, user_id } = userInfoByLogin.value || {};
            const uid = userId || user_id || user?.user_id || Cookies.get('userId');

            sendLog({
                type: 'click',
                name: logActionName,
                value: {
                    user_id: uid,
                    kwai_id: user?.kwai_id,
                    author_id: feed?.value?.user_id_str || feed?.value?.user_id,
                    author_kwai_id: feed?.value?.kwai_id,
                    photo_id: feed?.value?.photo_id_str,
                    play_time: `${Math.ceil(props?.videoRef?.getCurrentTime?.() || 0) * 1000}`,
                    photo_time: `${Math.ceil(props?.videoRef?.getDuration?.() || 0) * 1000}`,
                    llsid: getLlsidFromExptag(feed?.value?.exp_tag),
                    type: 'PHOTO',
                    exp_tag: feed?.value?.exp_tag,
                    identity: feed?.value?.photo_id_str,
                    index: props?.currentIndex,
                },
            });
        };

        const actionList = computed(() => {
            const newFeed = getFeedByPhotoId(props.photoId);
            const actionList: Array<ActionItem> = [
                {
                    type: ActionType.Like,
                    icon: IconMap[ActionType.Like],
                    count: formateNum(newFeed?.like_count || 0),
                    hidden: newFeed?.liked === FeedLiked.Like,
                    onClick: () => {
                        // showNotificationPopup();

                        sendActionLog(ActionType.Like);
                        setupConfig.handleLikeAction(ActionType.Like);
                    },
                },
                {
                    type: ActionType.UnLike,
                    icon: IconMap[ActionType.UnLike],
                    count: formateNum(newFeed?.like_count || 0),
                    hidden: newFeed?.liked === FeedLiked.UnLike,
                    onClick: () => {
                        // showNotificationPopup();

                        setupConfig.handleLikeAction(ActionType.UnLike);
                    },
                },
                {
                    type: ActionType.Comment,
                    icon: IconMap[ActionType.Comment],
                    count: formateNum(newFeed?.comment_count || 0),
                    onClick: () => {
                        // showNotificationPopup();

                        sendActionLog(ActionType.Comment);
                        commentListVisible.value = true;
                    },
                },
                {
                    type: ActionType.Share,
                    icon: IconMap[ActionType.Share],
                    count: formateNum(newFeed?.forward_count || 0),
                    onClick: () => {
                        // showNotificationPopup();

                        sendActionLog(ActionType.Share);
                        if (!props.photoId) {
                            return;
                        }

                        let copyUrl = `${window?.location?.origin}/@${feed.value?.user_name || 'kwai'}/video/${props.photoId || ''}${
                            window?.location?.search
                        }`;

                        if (props.feedPage === FeedPageEnum.DiscoverSearch) {
                            let searchWord = proxy?.$route?.params?.pathMatch;
                            if (!searchWord) {
                                const url = window.location.href;
                                const match = url.match(/\/search\/(.*?)\/video/);
                                searchWord = match?.[1];
                            }
                            const searchWordPath = searchWord ? `${searchWord}/` : '';
                            copyUrl = `${window?.location?.origin}/search/${searchWordPath}video/${props.photoId || ''}${window?.location?.search}`;
                        }

                        copyToClipboard(copyUrl);
                        Toast({
                            message: proxy?.$root?.$t?.('k_446636'),
                        });
                    },
                },
            ];

            return actionList;
        });

        const handleCommentListClose = () => {
            commentListVisible.value = false;
        };

        const handleFollowAction = debounce(async () => {
            // showNotificationPopup();

            sendActionLog(ActionType.Follow);
            const res = await FeedApi.followAction(
                {
                    fType: 1,
                    toUid: feed?.value?.user_id || `@${feed?.value?.kwai_id}`,
                    photoId: feed?.value?.photo_id_str,
                },
                {
                    params: {
                        'kwaipro.pwa_ph': Cookies.get('kwaipro.pwa_ph'),
                    },
                }
            );

            if (res?.result !== 1) {
                if (res?.result === 109) {
                    // eslint-disable-next-line vue/require-explicit-emits
                    ctx.emit('onChangeLoginPopupVisible', true);
                    return;
                }

                res?.error_msg &&
                    Toast({
                        message: res?.error_msg,
                    });
                return;
            }

            const newFeedList = feedList.value?.map?.(feed => {
                if (feed.photo_id_str === props?.photoId) {
                    return {
                        ...feed,
                        following: AuthorFollowing.Following,
                    };
                }
                return feed;
            });

            /**
             * @zh 关注 1s 后消失 icon
             */
            const delayTimeAfterFollowAction = 1000;
            isFollowingDuration.value = true;
            setTimeout(() => {
                proxy?.$store?.commit?.('feedMoudle/setFeedList', newFeedList);
                isFollowingDuration.value = false;
            }, delayTimeAfterFollowAction);
        }, 300);

        const handleViewUser = async () => {
            // if (isABTestExpGroup.value) {
            //     showNotificationPopup();
            // }
            if (!feed?.value?.kwai_id) {
                return;
            }
            replaceRoutePhotoId(proxy).finally(() => {
                proxy?.$router?.push?.({ path: `/@${feed?.value?.kwai_id}` });
            });
        };

        return {
            formateNum,
            handleCommentListClose,
            handleFollowAction,
            handleViewUser,

            IconMap,
            ActionType,
            actionList,
            commentListVisible,
            commentList,
            feed,
            isFollowingDuration,
            AuthorFollowing,
            isFromProfile,

            templateConfig: getTemplateConfig(),
        };
    },
});

export default ActionBar;
